if (!customElements.get("cookie-banner")) {
  class CookieBanner extends HTMLElement {
    static COOKIE_NAME = "mobieo_cookie_consent";

    connectedCallback() {
      this.expiryMonths = Number(this.dataset.expiryMonths) || 12;
      this.onlyConsentRegions = this.dataset.visibility === "shopify_regions";
      this.forceShow = this.hasAttribute("data-design-mode");
      this.hasOverlay = this.classList.contains("cookie-banner--overlay");
      this.dialog = this.querySelector(".cookie-banner__dialog");

      this.querySelector("[data-cookie-accept]")?.addEventListener("click", () =>
        this.saveChoice(true),
      );
      this.querySelector("[data-cookie-decline]")?.addEventListener(
        "click",
        () => this.saveChoice(false),
      );
      this.querySelector("[data-cookie-close]")?.addEventListener("click", () =>
        this.saveChoice(false),
      );

      this.onOpenRequest = this.onOpenRequest.bind(this);
      document.addEventListener("click", this.onOpenRequest);

      this.privacyApi = this.loadPrivacyApi();
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.onOpenRequest);
      if (this.hasOverlay) document.documentElement.style.overflow = "";
    }

    async init() {
      if (this.forceShow) {
        this.open();
        return;
      }

      if (this.onlyConsentRegions) {
        const api = await this.privacyApi;
        if (api && typeof api.shouldShowBanner === "function") {
          if (api.shouldShowBanner()) this.open();
          return;
        }
      }

      if (!this.getStoredChoice()) this.open();
    }

    loadPrivacyApi() {
      return new Promise((resolve) => {
        const shopify = window.Shopify;
        if (shopify?.customerPrivacy) {
          resolve(shopify.customerPrivacy);
          return;
        }
        if (typeof shopify?.loadFeatures !== "function") {
          resolve(null);
          return;
        }

        const timeout = setTimeout(() => resolve(null), 5000);
        shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          (error) => {
            clearTimeout(timeout);
            resolve(error ? null : window.Shopify.customerPrivacy || null);
          },
        );
      });
    }

    async saveChoice(accepted) {
      this.setStoredChoice(accepted ? "accepted" : "declined");
      this.close();

      const api = await this.privacyApi;
      if (api && typeof api.setTrackingConsent === "function") {
        api.setTrackingConsent(
          {
            analytics: accepted,
            marketing: accepted,
            preferences: accepted,
            sale_of_data: accepted,
          },
          () => {},
        );
      }

      document.dispatchEvent(
        new CustomEvent("cookie-consent:change", { detail: { accepted } }),
      );
    }

    getStoredChoice() {
      const match = document.cookie.match(
        new RegExp(`(?:^|; )${CookieBanner.COOKIE_NAME}=([^;]*)`),
      );
      return match ? match[1] : null;
    }

    setStoredChoice(value) {
      const maxAge = this.expiryMonths * 30 * 24 * 60 * 60;
      const secure = location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${CookieBanner.COOKIE_NAME}=${value}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;
    }

    open() {
      clearTimeout(this.hideTimer);
      this.hidden = false;
      if (this.hasOverlay) document.documentElement.style.overflow = "hidden";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add("is-visible"));
      });
      if (this.hasOverlay) {
        (this.querySelector(".cookie-banner__btn") || this.dialog)?.focus({
          preventScroll: true,
        });
      }
    }

    close() {
      this.classList.remove("is-visible");
      if (this.hasOverlay) document.documentElement.style.overflow = "";
      const duration =
        parseFloat(getComputedStyle(this).getPropertyValue("--cb-duration")) ||
        0;
      this.hideTimer = setTimeout(() => {
        this.hidden = true;
      }, duration);
    }

    onOpenRequest(event) {
      const trigger = event.target.closest(
        "[data-cookie-banner-open], a[href='#cookie-settings']",
      );
      if (!trigger) return;
      event.preventDefault();
      this.open();
    }
  }

  customElements.define("cookie-banner", CookieBanner);
}
